import { useStore as useJotaiStore } from 'jotai';
import { useEffect } from 'react';
import { useStore as useReduxStore } from 'react-redux';
import { useLocation } from 'react-router-dom';

import {
    activePromptAtom,
    isPromptVisibleAtom,
    lastPromptClosedAtAtom,
    pendingRecordAtom,
} from '@/atoms/exitIntentSavePromptAtoms';
import { usePrevious } from '@/hooks/usePrevious';
import { useAppSelector } from '@/redux/hooks';
import { getSavedItemsCount, isItemSaved } from '@/redux/modules/item/saved/savedItems.selectors';
import { GlobalState } from '@/redux/store';
import { COOLDOWN_MS, GLOBAL_CAP, PROMPT_DELAY_MS } from '@/utils/exitIntentSavePrompt/exitIntentSavePrompt.constants';
import { UnsavedViewRecord } from '@/utils/exitIntentSavePrompt/exitIntentSavePrompt.types';
import {
    getSessionPromptCount,
    getUserDismissed,
    getUserSaved,
    hasShownLot,
    readPending,
    setUserSaved,
} from '@/utils/exitIntentSavePrompt/exitIntentSavePromptStorage';
import { getIdFromPath, isItemPage } from '@/utils/urls';

const isExcludedDestination = (pathname: string, record: UnsavedViewRecord) => {
    if (
        pathname.includes('/console/') ||
        pathname.includes('/auth/') ||
        pathname.includes('/checkout/') ||
        pathname.includes('/checkoutATG/') ||
        pathname.includes('/loginRedirect') ||
        pathname.includes('/password_reset')
    ) {
        return true;
    }

    // Same LDP the record belongs to — no navigation actually occurred (LAAC-2038 AC 5, LAAC-2038 AC 22).
    return isItemPage(pathname) && getIdFromPath(pathname) === record.itemId;
};

/**
 * Controller for the exit-intent save prompt (LAAC-2038 spec Section 5 steps 4-6).
 *
 * This hook runs on every route change to evaluate if the "save prompt" should be shown:
 * 1. Retrieves the pending unsaved record from storage/memory.
 * 2. Checks eligibility (cooldowns, user dismissals, excluded paths, etc.).
 * 3. If eligible, waits `PROMPT_DELAY_MS` before showing the prompt via `activePromptAtom`.
 *    - During this wait, it re-checks time-sensitive conditions (like cooldown/exclusivity).
 * 4. Clears the pending record immediately after evaluation to prevent duplicate triggers.
 *
 * It also monitors the global saved items count; if a user saves an item via any surface,
 * it sets a session flag to suppress further prompts.
 */
export const useExitIntentSavePromptController = () => {
    const { key, pathname } = useLocation();
    const reduxStore = useReduxStore<GlobalState>();
    const jotaiStore = useJotaiStore();

    const savedItemsCount = useAppSelector(getSavedItemsCount);
    const previousSavedItemsCount = usePrevious(savedItemsCount);

    useEffect(() => {
        if (previousSavedItemsCount !== undefined && savedItemsCount > previousSavedItemsCount) {
            setUserSaved();
        }
    }, [previousSavedItemsCount, savedItemsCount]);

    useEffect(() => {
        // Dwell arms in useLayoutEffect so the pending record is already set before this
        // passive effect runs on the destination route. Prefer the in-memory atom; fall back
        // to the sessionStorage mirror for hard-refresh / tests that seed via writePending().
        const record = jotaiStore.get(pendingRecordAtom) ?? readPending();
        if (!record) {
            return;
        }

        const isBusyNow =
            jotaiStore.get(isPromptVisibleAtom) || Date.now() - jotaiStore.get(lastPromptClosedAtAtom) < COOLDOWN_MS;
        const isIneligible =
            isBusyNow ||
            getUserSaved() ||
            getUserDismissed() ||
            isExcludedDestination(pathname, record) ||
            hasShownLot(record.itemId) ||
            getSessionPromptCount() >= GLOBAL_CAP ||
            isItemSaved(reduxStore.getState(), record.itemId);

        // Write-through atom clears sessionStorage as well.
        jotaiStore.set(pendingRecordAtom, null);

        if (isIneligible) {
            return;
        }

        const timer = setTimeout(() => {
            const stillBusy =
                jotaiStore.get(isPromptVisibleAtom) ||
                Date.now() - jotaiStore.get(lastPromptClosedAtAtom) < COOLDOWN_MS;

            if (stillBusy) {
                return;
            }

            jotaiStore.set(activePromptAtom, record);
        }, PROMPT_DELAY_MS);

        return () => clearTimeout(timer);
        // Navigation is the primary trigger.
        // Reactive state values are excluded from deps as they are read via
        // stable store references to avoid clearing pending records on every update
    }, [pathname, key, jotaiStore, reduxStore]);
};
